import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Camera, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { StatusBadge, PriorityBadge, ServiceIcon, LoadingSpinner } from '../common/Badges';

const CustomerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await api('/api/service-requests');
      setRequests(data);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const viewPhotos = async (request) => {
    setSelectedRequest(request);
    setLoadingPhotos(true);
    try {
      const [photoData, logData] = await Promise.all([
        api(`/api/work-photos?request_id=${request.id}`),
        api(`/api/work-logs?request_id=${request.id}`)
      ]);
      setPhotos(photoData);
      setWorkLogs(logData);
    } catch {
      setPhotos([]); setWorkLogs([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="customer-requests" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-white">My Service Requests</h1>
        <Link to="/customer/new-request" className="btn-primary" data-testid="new-request-btn">
          <Plus className="w-4 h-4 inline mr-2" />
          New Request
        </Link>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Ticket</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Service</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Priority</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Photos</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-white">Price</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {requests.map((request) => (
              <tr key={request.id} className="hover:bg-gray-800" data-testid={`request-row-${request.id}`}>
                <td className="px-4 py-3 font-mono text-sm">{request.ticket_number}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <ServiceIcon type={request.service_type} className="w-4 h-4 text-white" />
                    <span className="capitalize">{request.service_type.replace('_', ' ')}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={request.status} /></td>
                <td className="px-4 py-3"><PriorityBadge priority={request.priority} /></td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(request.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => viewPhotos(request)}
                      className="text-sm text-red-500 hover:text-red-400 flex items-center gap-1"
                      data-testid={`view-photos-btn-${request.id}`}
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Details
                    </button>
                    <Link
                      to={`/customer/blueprint/${request.id}`}
                      className="text-sm text-blue-500 hover:text-blue-400 flex items-center gap-1"
                      data-testid={`view-blueprint-btn-${request.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Blueprint
                    </Link>
                  </div>
                </td>
                <td className="px-4 py-3 font-medium text-green-400">
                  {request.price ? `$${request.price.toFixed(2)}` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {requests.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            No service requests found.
          </div>
        )}
      </div>

      {/* Work Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="work-photos-modal">
          <div className="card p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-white">Job Details</h3>
                <p className="text-sm text-gray-400">{selectedRequest.ticket_number} - {selectedRequest.service_type.replace('_', ' ')}</p>
                {selectedRequest.price && <p className="text-lg font-bold text-green-400 mt-1">Total: ${selectedRequest.price.toFixed(2)}</p>}
                {selectedRequest.price_notes && <p className="text-sm text-gray-400">{selectedRequest.price_notes}</p>}
              </div>
              <button onClick={() => setSelectedRequest(null)} className="p-2 hover:bg-gray-900">
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingPhotos ? <LoadingSpinner /> : (
              <>
                {workLogs.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">Work Performed</h4>
                    <div className="space-y-3">
                      {workLogs.map((log) => (
                        <div key={log.id} className="bg-gray-800/50 p-4 rounded" data-testid={`work-log-${log.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-white">{log.service_performed}</p>
                            <p className="text-xs text-gray-500">{new Date(log.created_at).toLocaleDateString()}</p>
                          </div>
                          <p className="text-sm text-gray-400">{log.description}</p>
                          {log.materials_used && <p className="text-xs text-gray-500 mt-1">Materials: {log.materials_used}</p>}
                          <p className="text-xs text-gray-500 mt-1">Technician: {log.technician_name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {photos.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-300 mb-3">Work Photos</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {photos.map((photo) => (
                        <div key={photo.id} className="group relative">
                          <img src={photo.image_url} alt={photo.caption || 'Work photo'} className="w-full h-48 object-cover rounded border border-gray-700" />
                          <div className="mt-2">
                            {photo.caption && <p className="text-sm text-gray-300">{photo.caption}</p>}
                            <p className="text-xs text-gray-500">By {photo.technician_name} - {new Date(photo.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Camera className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                    <p className="text-sm">No work photos yet.</p>
                  </div>
                )}

                {workLogs.length === 0 && photos.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <p>No work details available yet for this request.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRequests;
